// Modules
import { cloneElement, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Types
import type { RefObject } from 'react';
import type { TTooltipProps } from './Tooltip.type';

// Components
import TooltipDialogue from './TooltipDialogue';

// Helpers
import { getTooltipPosition } from './Tooltip.helper';

// Constants
import { TOOLTIP_DEFAULT_PROPS } from './Tooltip.constant';

const Tooltip = (props: TTooltipProps) => {
	const {
		children: target,
		className,
		content,
		noArrow,
		position = TOOLTIP_DEFAULT_PROPS.position,
		ref,
		...restProps
	} = props
	const localRef = useRef<HTMLDivElement | null>(null);
	const tooltipRef = ref as RefObject<HTMLDivElement> || localRef;
	const targetRef = useRef<HTMLDivElement | null>(null);

	const [isShowTooltip, setIsShowTooltip] = useState<boolean>(true)

	const clonedTarget = cloneElement(target!, {
		...target.props,
		ref: targetRef,
		onMouseEnter: (e: React.MouseEvent) => {
			target.props.onMouseEnter?.(e)
			setIsShowTooltip(true)
		},
		onMouseLeave: (e: React.MouseEvent) => {
			target.props.onMouseLeave?.(e)
			setIsShowTooltip(false)
		}
	})

	const [tooltipTop, setTooltipTop] = useState<number>(0)
	const [tooltipLeft, setTooltipLeft] = useState<number>(0)

	useEffect(() => {
		setIsShowTooltip(false)
	}, [])

	useEffect(() => {
		const { top, left } = getTooltipPosition({
			position: position!,
			targetHeight: targetRef.current?.offsetHeight || 0,
			targetWidth: targetRef.current?.offsetWidth || 0,
			targetTop: targetRef.current?.offsetTop || 0,
			targetLeft: targetRef.current?.offsetLeft || 0,
			tooltipHeight: tooltipRef?.current?.offsetHeight || 0,
			tooltipWidth: tooltipRef?.current?.offsetWidth || 0
		})

		setTooltipTop(top)
		setTooltipLeft(left)
	}, [targetRef, tooltipRef, position])

	return (
		<>
			{createPortal(
				isShowTooltip && (
					<TooltipDialogue
						ref={tooltipRef}
						className={className}
						position={position}
						noArrow={noArrow}
						style={{
							top: tooltipTop,
							left: tooltipLeft
						}}
						{...restProps}
					>
						{content}
					</TooltipDialogue>
				),
				document.body
			)}
			{clonedTarget}
		</>
	)
}

export default Tooltip
